import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { SDKStudy } from '@axon/rosetta-sdk';
import { LibraryEndpoint, LibraryResourceStatus } from 'libs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
const parseStudyConfigMetadata = (config) => ({
    ...config,
    ...(typeof config.metadata === 'string' && { metadata: JSON.parse(config.metadata) }),
});
const extractReportKeysFromStudyConfig = (studyConfig) => {
    if (!studyConfig?.data) {
        return [];
    }
    try {
        const study = SDKStudy.fromRosetta(studyConfig.data);
        return (study.reports ?? [])
            .map((r) => r.libraryResourceKey)
            .filter((key) => !!key);
    }
    catch {
        return [];
    }
};
/**
 * Filters test environment options to only include environments whose
 * study config version contains the given report. Environments without
 * a studyConfigVersion are excluded because availability cannot be verified.
 *
 * For reports that are not version-locked (not present in any study config),
 * all environments are returned unchanged.
 */
export const useReportEnvironmentOptions = (studyId, reportId, devEnvironments, testEnvironmentsOptions) => {
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const { data: allStudyConfigs, isLoading, isError, } = useQuery({
        queryKey: ['study_config', studyId, 'all_versions'],
        queryFn: async () => {
            const searchParams = new URLSearchParams({ studyId: String(studyId) });
            searchParams.append('status', LibraryResourceStatus.APPROVED);
            searchParams.append('status', LibraryResourceStatus.PUBLISHED);
            const { data: response } = await libraryClient.get(`${LibraryEndpoint.GET_STUDY_CONFIGS}?${searchParams.toString()}`);
            return response.data.map(parseStudyConfigMetadata);
        },
        enabled: !!studyId && !!reportId,
    });
    const filteredOptions = useMemo(() => {
        if (isError) {
            return [];
        }
        if (!allStudyConfigs || !testEnvironmentsOptions?.length) {
            return testEnvironmentsOptions ?? [];
        }
        const versionReportKeysMap = new Map();
        for (const config of allStudyConfigs) {
            versionReportKeysMap.set(config.version, extractReportKeysFromStudyConfig(config));
        }
        const isVersionLockedReport = Array.from(versionReportKeysMap.values()).some((keys) => keys.includes(reportId));
        if (!isVersionLockedReport) {
            return testEnvironmentsOptions;
        }
        return testEnvironmentsOptions.filter((option) => {
            const env = devEnvironments.find((e) => e.cortexId === option.value);
            if (!env) {
                return false;
            }
            const configVersion = env.metadata?.studyConfigVersion;
            if (configVersion == null) {
                return false;
            }
            const reportKeys = versionReportKeysMap.get(Number(configVersion));
            return reportKeys?.includes(reportId) ?? false;
        });
    }, [allStudyConfigs, isError, testEnvironmentsOptions, devEnvironments, reportId]);
    return { filteredOptions, isLoading };
};
